import { useState, useMemo, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { PricingAdmin } from "@/components/PricingAdmin";
import { SkuMappingAdmin } from "@/components/SkuMappingAdmin";
import { ValidationControlPanel } from "@/components/ValidationControlPanel";
import { DocumentFormEngine } from "@/components/DocumentFormEngine";
import { LocalPresenceAdmin } from "@/components/LocalPresenceAdmin";
import { FinancialVisibilitySettings } from "@/components/FinancialVisibilitySettings";
import { MessagingAutomationSettings as MessagingAutomationSettingsPanel } from "@/components/MessagingAutomationSettings";
import { VendorLeadTimesAdmin } from "@/components/VendorLeadTimesAdmin";
import { InstallerExecutionSettings } from "@/components/InstallerExecutionSettings";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { useBranding, CCC_BLUE_DEFAULT } from "@/data/BrandingContext";
import {
  Palette, Upload, Building2, Users, CreditCard, Plug, Shield,
  Key, Globe, CheckCircle, AlertTriangle, Settings as SettingsIcon,
  Calendar, DollarSign, Search, Plus, MoreHorizontal,
  Lock, Bell, Clock, Mail, MapPin, Phone, FileText, Eye, History,
  Pencil, ToggleLeft, ToggleRight, Trash2, UserX, UserCheck, Package,
  ArrowUpDown, Check, X, ChevronRight, Zap, Crown, Rocket, ShieldOff, PhoneOutgoing, MessageSquare,
  RotateCcw as RotateCcwIcon,
} from "lucide-react";
import type { UserRole } from "@/data/RoleContext";
import { ROLES, useRole } from "@/data/RoleContext";
import { PermissionGate } from "@/components/PermissionGate";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { SettingsService, ProfileRow, BranchRow, DEFAULT_ROLE_PERMISSIONS, RolePermissionsSettings, AuditLogEntry } from "@/services/settings.service";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import { getPasswordResetRedirectUrl } from "@/lib/authRedirects";

/* ── User type for UI ── */
interface UserItem {
  id: string;
  name: string;
  email: string;
  role: string;
  status: "Active" | "Invited" | "Deactivated";
  lastLogin: string;
  branch: string;
}

/* ── Role permission matrix ── */
const PERMISSION_GROUPS = [
  {
    section: "Jobs & Projects",
    perms: ["View Jobs", "Create Jobs", "Edit Jobs", "Delete Jobs", "Approve QA", "Close Jobs", "View Job Financials"],
  },
  {
    section: "Scheduling & Dispatch",
    perms: ["View Scheduling", "Manage Scheduling", "Assign Crew", "Reschedule Jobs"],
  },
  {
    section: "Issues",
    perms: ["View Issues", "Manage Issues", "Resolve Issues"],
  },
  {
    section: "Procurement & Warehouse",
    perms: ["View Ordering", "Create Purchase Orders", "Approve Purchase Orders", "View Inventory", "Manage Inventory", "Mark Materials Ready"],
  },
  {
    section: "Execution",
    perms: ["View Execution", "Manage Execution", "Mark Phase Complete"],
  },
  {
    section: "Network / Contractors",
    perms: ["Manage Contractors", "Approve Contractor Onboarding", "Manage Installer Offers"],
  },
  {
    section: "Customers & Pipeline",
    perms: ["View Customers", "View Pipeline", "Manage Pipeline", "Manage Lead Sources"],
  },
  {
    section: "Financials",
    perms: ["View Financials", "Manage Financials", "View Commissions", "Manage Commissions", "Approve Payments"],
  },
  {
    section: "Reports",
    perms: ["View Reports", "Export Reports"],
  },
  {
    section: "Users & Settings",
    perms: ["Manage Users", "Manage Settings", "Manage Roles & Permissions"],
  },
] as const;

const PERMISSIONS = PERMISSION_GROUPS.flatMap((g) => g.perms);


const STATUS_STYLES: Record<string, string> = {
  Active: "bg-success/10 text-success border-success/20",
  Invited: "bg-info/10 text-info border-info/20",
  Deactivated: "bg-muted text-muted-foreground border-border",
};

/* ── Branch type for UI ── */
interface BranchItem {
  id: string;
  name: string;
  region: string;
  status: "Active" | "Inactive";
  address: string;
}

/* ── Audit log seed ── */
const AREA_STYLES: Record<string, { icon: React.ElementType; color: string }> = {
  Users: { icon: Users, color: "text-info" },
  Roles: { icon: Shield, color: "text-warning" },
  Company: { icon: Building2, color: "text-primary" },
  Branding: { icon: Palette, color: "text-accent" },
  Preferences: { icon: SettingsIcon, color: "text-muted-foreground" },
  Billing: { icon: CreditCard, color: "text-success" },
  Integrations: { icon: Plug, color: "text-info" },
  Pricing: { icon: DollarSign, color: "text-warning" },
};

interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  area: string;
  summary: string;
}

function formatAuditSummary(entry: AuditLogEntry): string {
  if (entry.action === "create") return "Created a new job";
  if (!entry.changed_fields) return "Updated job record";
  const keys = Object.keys(entry.changed_fields);
  if (keys.length === 0) return "Updated job record";
  const readable: Record<string, string> = {
    status: "status", stage: "stage", assigned_rep: "assigned rep",
    install_date: "install date", notes: "notes", address: "address",
    customer_name: "customer name", job_type: "job type",
    material_status: "material status", qa_status: "QA status",
  };
  const labels = keys.map((k) => readable[k] ?? k.replace(/_/g, " "));
  const first = labels.slice(0, 3).join(", ");
  const extra = labels.length > 3 ? ` and ${labels.length - 3} more` : "";
  // Show old → new for single-field changes
  if (keys.length === 1) {
    const k = keys[0];
    const diff = entry.changed_fields[k];
    const oldVal = diff?.old != null ? String(diff.old) : "—";
    const newVal = diff?.new != null ? String(diff.new) : "—";
    return `Updated ${labels[0]}: "${oldVal}" → "${newVal}"`;
  }
  return `Updated ${first}${extra}`;
}

const AUDIT_LOG: AuditEntry[] = [
  { id: "a1", timestamp: "2026-03-26 14:32", user: "Mike Reynolds", area: "Branding", summary: "Updated primary color from #1E40AF to #2563EB" },
  { id: "a2", timestamp: "2026-03-26 11:15", user: "Sarah Chen", area: "Users", summary: "Invited Chris Delaney (Sales) to Lakeland branch" },
  { id: "a3", timestamp: "2026-03-25 16:45", user: "Mike Reynolds", area: "Billing", summary: "Updated billing contact email to billing@koredor.com" },
  { id: "a4", timestamp: "2026-03-25 10:20", user: "James Porter", area: "Preferences", summary: "Enabled auto-assignment for Ready for Dispatch jobs" },
  { id: "a5", timestamp: "2026-03-24 09:05", user: "Sarah Chen", area: "Roles", summary: "Added 'Manage Inventory' permission to Warehouse Manager role" },
  { id: "a6", timestamp: "2026-03-23 15:30", user: "Mike Reynolds", area: "Integrations", summary: "Configured BoxHero webhook endpoint for inventory sync" },
  { id: "a7", timestamp: "2026-03-23 11:00", user: "Sarah Chen", area: "Users", summary: "Deactivated Linda Torres account" },
  { id: "a8", timestamp: "2026-03-22 14:10", user: "Mike Reynolds", area: "Company", summary: "Added Lakeland branch to Central region" },
  { id: "a9", timestamp: "2026-03-21 09:45", user: "James Porter", area: "Pricing", summary: "Updated Walk-in Shower base price from $8,500 to $9,200" },
  { id: "a10", timestamp: "2026-03-20 16:20", user: "Sarah Chen", area: "Integrations", summary: "Connected GoHighLevel CRM for lead sync" },
  { id: "a11", timestamp: "2026-03-19 10:30", user: "Mike Reynolds", area: "Branding", summary: "Uploaded new company logo" },
  { id: "a12", timestamp: "2026-03-18 13:15", user: "Sarah Chen", area: "Users", summary: "Changed Tony Ramirez role from Sales to Installer" },
];

/* ── Automation Control Tab ── */

type AutomationGlobalMode = "assisted" | "automated";
/* ═══ ROUTING & DISPATCH SETTINGS ═══ */
function RoutingDispatchSettings() {
  const [maxDistance, setMaxDistance] = useState(30);
  const [preferTerritory, setPreferTerritory] = useState(true);
  const [clusterOptimization, setClusterOptimization] = useState(true);
  const [warningThreshold, setWarningThreshold] = useState<"moderate" | "strict">("moderate");

  return (
    <div className="space-y-4">
      <Card className="shadow-none">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm">Routing & Dispatch Settings</CardTitle>
            <Badge variant="outline" className="text-[9px] px-1.5 py-0">Beta · Assistive Only</Badge>
          </div>
          <CardDescription className="text-xs">
            Configure how the system suggests appointment routing and rep assignments. These settings guide suggestions only — no auto-booking or auto-assignment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Max Travel Distance */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Maximum Travel Distance</Label>
            <div className="flex items-center gap-4">
              <Slider
                value={[maxDistance]}
                onValueChange={([v]) => setMaxDistance(v)}
                min={10}
                max={60}
                step={5}
                className="flex-1"
              />
              <span className="text-sm font-bold w-16 text-right">{maxDistance} mi</span>
            </div>
            <p className="text-[10px] text-muted-foreground">Bookings beyond this distance will show a travel warning.</p>
          </div>

          {/* Territory Preference */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-xs font-semibold">Prefer Territory Match</p>
              <p className="text-[10px] text-muted-foreground">Prioritize reps within their assigned coverage zones.</p>
            </div>
            <Switch checked={preferTerritory} onCheckedChange={setPreferTerritory} />
          </div>

          {/* Cluster Optimization */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-xs font-semibold">Route Clustering</p>
              <p className="text-[10px] text-muted-foreground">Suggest time slots near a rep's existing appointments to reduce travel.</p>
            </div>
            <Switch checked={clusterOptimization} onCheckedChange={setClusterOptimization} />
          </div>

          {/* Warning Threshold */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Travel Warning Sensitivity</Label>
            <Select value={warningThreshold} onValueChange={(v) => setWarningThreshold(v as "moderate" | "strict")}>
              <SelectTrigger className="h-9 w-[200px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="moderate">Moderate — warn at 75% max distance</SelectItem>
                <SelectItem value="strict">Strict — warn at 50% max distance</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Scheduling Mode */}
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="h-3.5 w-3.5 text-primary" />
              <p className="text-xs font-bold text-primary">Scheduling Mode: Assistive</p>
              <Badge className="text-[8px] px-1.5 py-0 bg-primary/10 text-primary border-primary/20" variant="outline">Locked · Beta</Badge>
            </div>
            <p className="text-[10px] text-muted-foreground">
              During beta, all routing and scheduling suggestions require manual review. Auto-assignment and auto-booking are disabled.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Rep Territories */}
      <Card className="shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" /> Rep Territories
          </CardTitle>
          <CardDescription className="text-xs">Coverage zones and home base locations for each sales rep.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[
              { name: "Mike Johnson", base: "33606", region: "Tampa", zones: ["336", "335"] },
              { name: "Sarah Chen", base: "33609", region: "Tampa", zones: ["336", "335", "337"] },
              { name: "David Williams", base: "34747", region: "Orlando", zones: ["347", "348", "328"] },
              { name: "Emily Parker", base: "33611", region: "Tampa Bay", zones: ["336", "337", "338"] },
              { name: "James Rivera", base: "34102", region: "Naples", zones: ["341", "339"] },
            ].map(rep => (
              <div key={rep.name} className="flex items-center justify-between rounded-lg border p-2.5">
                <div className="flex items-center gap-3">
                  <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                    {rep.name.split(" ").map(n => n[0]).join("")}
                  </div>
                  <div>
                    <p className="text-xs font-semibold">{rep.name}</p>
                    <p className="text-[10px] text-muted-foreground">{rep.region} · Base: {rep.base}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {rep.zones.map(z => (
                    <Badge key={z} variant="outline" className="text-[9px] px-1.5 py-0">{z}xx</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

type DomainMode = "suggest_only" | "auto_execute";

function AutomationControlTab() {
  const [globalMode, setGlobalMode] = useState<AutomationGlobalMode>("assisted");
  const [messagingMode, setMessagingMode] = useState<DomainMode>("suggest_only");
  const [followUpMode, setFollowUpMode] = useState<DomainMode>("suggest_only");

  const handleGlobalChange = (mode: AutomationGlobalMode) => {
    if (mode === "automated") {
      toast.info("Automated Mode is not available during beta", { description: "All actions will continue to require confirmation." });
      return;
    }
    setGlobalMode(mode);
    toast.success("Global mode set to Assisted");
  };

  const handleDomainChange = (domain: string, mode: DomainMode) => {
    if (mode === "auto_execute") {
      toast.info("Auto Execute is not available during beta", { description: "This domain will remain in Suggest Only mode." });
      return;
    }
    if (domain === "messaging") setMessagingMode(mode);
    if (domain === "follow_ups") setFollowUpMode(mode);
    toast.success(`${domain === "messaging" ? "Messaging" : "Follow-Ups"} set to Suggest Only`);
  };

  return (
    <div className="space-y-4">
      <Card className="shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-primary" /> Global Automation Mode
          </CardTitle>
          <CardDescription className="text-xs">
            Controls how the system handles suggested actions across all domains.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {([
            { value: "assisted" as const, label: "Assisted Mode", desc: "System suggests actions — you confirm before anything happens", badge: "Default" },
            { value: "automated" as const, label: "Automated Mode", desc: "System can execute allowed actions automatically (coming soon)", badge: "Beta Locked" },
          ]).map(opt => (
            <button
              key={opt.value}
              onClick={() => handleGlobalChange(opt.value)}
              className={`w-full text-left rounded-lg border px-4 py-3 transition-colors ${
                globalMode === opt.value
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-muted/30"
              } ${opt.value === "automated" ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-foreground">{opt.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{opt.desc}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">{opt.badge}</Badge>
                  {globalMode === opt.value && <CheckCircle className="h-4 w-4 text-primary" />}
                </div>
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card className="shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <ToggleLeft className="h-4 w-4 text-primary" /> Domain Controls
          </CardTitle>
          <CardDescription className="text-xs">
            Set automation behavior per domain. All domains default to Suggest Only during beta.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {([
            { key: "messaging", label: "Messaging", desc: "Outbound SMS, email, and customer notifications", icon: MessageSquare, mode: messagingMode },
            { key: "follow_ups", label: "Follow-Ups", desc: "Disposition follow-up tasks and reminders", icon: Bell, mode: followUpMode },
          ]).map(domain => (
            <div key={domain.key} className="rounded-lg border px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <domain.icon className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{domain.label}</p>
                    <p className="text-[11px] text-muted-foreground">{domain.desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={`text-[9px] ${
                    domain.mode === "suggest_only" ? "bg-primary/5 text-primary border-primary/20" : "bg-muted"
                  }`}>
                    {domain.mode === "suggest_only" ? "Suggest Only" : "Auto Execute"}
                  </Badge>
                  <Select
                    value={domain.mode}
                    onValueChange={(v) => handleDomainChange(domain.key, v as DomainMode)}
                  >
                    <SelectTrigger className="w-[130px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="suggest_only" className="text-xs">Suggest Only</SelectItem>
                      <SelectItem value="auto_execute" className="text-xs">Auto Execute</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="shadow-none border-primary/20 bg-primary/[0.02]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Shield className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-foreground">Beta Safety</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                During beta, all actions require user confirmation. No messages are auto-sent and no follow-ups are auto-executed.
                Auto Execute mode will be available in a future release.
              </p>
              <div className="flex items-center gap-3 mt-2">
                <Badge variant="outline" className="text-[9px] bg-success/5 text-success border-success/20 gap-1">
                  <CheckCircle className="h-2.5 w-2.5" /> Requires Review
                </Badge>
                <Badge variant="outline" className="text-[9px] bg-success/5 text-success border-success/20 gap-1">
                  <CheckCircle className="h-2.5 w-2.5" /> No Auto-Send
                </Badge>
                <Badge variant="outline" className="text-[9px] bg-success/5 text-success border-success/20 gap-1">
                  <CheckCircle className="h-2.5 w-2.5" /> User Confirms All
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ── Tab label map ── */
const TAB_TITLES: Record<string, string> = {
  users: "Users",
  roles: "Roles & Permissions",
  company: "Company",
  branding: "Branding",
  preferences: "Preferences",
  billing: "Billing & Subscription",
  integrations: "Integrations",
  pricing: "Pricing",
  contracts: "Documents & Forms",
  audit: "Audit Log",
  "local-presence": "Local Presence",
  "automation": "Automation",
};

/* ── Billing plans ── */
const PLANS = [
  { id: "starter", name: "Starter", price: 99, users: 5, branches: 1, features: ["Core job tracking", "Basic scheduling", "QA checklists"], icon: Rocket },
  { id: "professional", name: "Professional", price: 299, users: 25, branches: 10, features: ["All Starter features", "Multi-branch", "Inventory", "Contractor network", "Pricing engine"], icon: Zap, current: true },
  { id: "enterprise", name: "Enterprise", price: 599, users: 100, branches: 50, features: ["All Professional features", "API access", "White-label", "Priority support", "Custom integrations"], icon: Crown },
];

export default function Settings() {
  const { branding, setBranding } = useBranding();
  const { role } = useRole();
  const [activeTab, setActiveTab] = useState("users");
  const [draft, setDraft] = useState(branding);
  const [savingBranding, setSavingBranding] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [liveAuditLog, setLiveAuditLog] = useState<AuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(true);
  const [companyInfo, setCompanyInfo] = useState({
    legalName: "", dbaName: "", ein: "", licenseNumber: "",
    phone: "", email: "", website: "", address: "",
  });
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState<string>("all");
  const canManageProducts = ["super_admin", "admin", "owner", "ops_manager", "it_staff"].includes(role);
  const canManageRoles = ["super_admin", "owner", "admin", "it_staff"].includes(role);

  /* ── Settings loading state ── */
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);

  const refreshUsers = async () => {
    try {
      const profiles = await SettingsService.getProfiles();
      setUsers(profiles.map((p: ProfileRow) => ({
        id: p.id,
        name: p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email,
        email: p.email,
        role: p.role || "staff",
        status: (p.status === "Active" || p.status === "Invited" || p.status === "Deactivated") ? p.status : "Active",
        lastLogin: p.last_login ? new Date(p.last_login).toISOString().split("T")[0] : "—",
        branch: p.branch_name || "—",
      })));
    } catch (err) {
      console.warn("Failed to refresh users:", err);
    }
  };
  const [branchesLoading, setBranchesLoading] = useState(true);

  /* ── Load settings from Supabase on mount ── */
  useEffect(() => {
    async function loadSettings() {
      try {
        const allSettings = await SettingsService.getAll();
        
        /* Update preferences state if DB has data */
        if (allSettings.preferences) {
          setPrefs({
            emailNewJobs: allSettings.preferences.emailNotifications ?? true,
            issueEscalation: allSettings.preferences.issueEscalation ?? true,
            dailyDigest: allSettings.preferences.dailyDigest ?? false,
            autoAdvance: allSettings.preferences.autoAdvance ?? false,
            requireQA: allSettings.preferences.qaRequirements ?? true,
            subBidNotify: true,
            showFinancials: false,
            defaultTimezone: allSettings.preferences.timezone ?? "America/New_York (EST)",
            defaultDateFormat: allSettings.preferences.dateFormat ?? "MM/DD/YYYY",
            defaultBranch: allSettings.preferences.defaultBranch ?? "Tampa HQ",
            businessHours: "7:00 AM – 6:00 PM",
          });
        }
        
        /* Update branding if DB has data */
        if (allSettings.branding) {
          const b = allSettings.branding;
          setDraft((prev) => ({
            ...prev,
            ...(b.companyName != null && { companyName: b.companyName as string }),
            ...(b.platformName != null && { platformName: b.platformName as string }),
            ...(b.tagline != null && { tagline: b.tagline as string }),
            ...(b.logoUrl !== undefined && { logoUrl: b.logoUrl as string | null }),
            ...(b.primaryHue != null && { primaryHue: b.primaryHue as number }),
            ...(b.primarySaturation != null && { primarySaturation: b.primarySaturation as number }),
            ...(b.primaryLightness != null && { primaryLightness: b.primaryLightness as number }),
            ...(b.accentHue != null && { accentHue: b.accentHue as number }),
            ...(b.accentSaturation != null && { accentSaturation: b.accentSaturation as number }),
            ...(b.accentLightness != null && { accentLightness: b.accentLightness as number }),
            ...(b.darkMode != null && { darkMode: b.darkMode as boolean }),
          }));
        }
        
        /* Update company info */
        if (allSettings.company) {
          setCompanyInfo({
            legalName: allSettings.company.legalName ?? "",
            dbaName: allSettings.company.dbaName ?? "",
            ein: allSettings.company.ein ?? "",
            licenseNumber: allSettings.company.licenseNumber ?? "",
            phone: allSettings.company.phone ?? "",
            email: allSettings.company.email ?? "",
            website: allSettings.company.website ?? "",
            address: allSettings.company.address ?? "",
          });
        }

        /* Update routing settings - local component state */
        if (allSettings.routing) {
          /* These are used in RoutingDispatchSettings component */
        }
        
      } catch (err) {
        console.warn("Failed to load settings from database, using defaults:", err);
      } finally {
        setSettingsLoading(false);
      }
    }

    async function loadUsers() {
      try {
      const profiles = await SettingsService.getProfiles();
      setUsers(profiles.map((p: ProfileRow) => ({
        id: p.id,
        name: p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ") || p.email,
        email: p.email,
        role: p.role || "staff",
        status: (p.status === "Active" || p.status === "Invited" || p.status === "Deactivated") ? p.status : "Active",
        lastLogin: p.last_login ? new Date(p.last_login).toISOString().split('T')[0] : "—",
        branch: p.branch_name || "—",
        })));
      } catch (err) {
        console.warn("Failed to load users from database:", err);
      } finally {
        setUsersLoading(false);
      }
    }

    async function loadBranches() {
      try {
        const branchesData = await SettingsService.getBranches();
        setBranches(branchesData.map((b: BranchRow) => ({
          id: b.id,
          name: b.name,
          region: b.region,
          status: b.is_active ? "Active" : "Inactive",
          address: b.municipality ? `${b.municipality}, ${b.state}` : b.region,
        })));
      } catch (err) {
        console.warn("Failed to load branches from database:", err);
      } finally {
        setBranchesLoading(false);
      }
    }

    async function loadRolePermissions() {
      setPermsLoading(true);
      try {
        const perms = await SettingsService.getRolePermissions();
        setRolePerms(perms);
      } catch {
        setRolePerms(DEFAULT_ROLE_PERMISSIONS);
      } finally {
        setPermsLoading(false);
      }
    }

    async function loadAuditLog() {
      try {
        const entries = await SettingsService.getAuditLog(200);
        setLiveAuditLog(entries);
      } catch (err) {
        console.warn("Failed to load audit log:", err);
      } finally {
        setAuditLoading(false);
      }
    }

    loadSettings();
    loadUsers();
    loadBranches();
    loadRolePermissions();
    loadAuditLog();
  }, []);

  /* ── User state ── */
  const [users, setUsers] = useState<UserItem[]>([]);
  const [editUserDialog, setEditUserDialog] = useState<UserItem | null>(null);
  const [editUserDraft, setEditUserDraft] = useState<Partial<UserItem>>({});
  const [inviteUserDialog, setInviteUserDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", fullName: "", role: "viewer", branch: "" });
  const [invitingUser, setInvitingUser] = useState(false);

  /* ── Roles state ── */
  const [rolePerms, setRolePerms] = useState<RolePermissionsSettings>({});
  const [permsLoading, setPermsLoading] = useState(true);
  const [savingPerms, setSavingPerms] = useState(false);

  /* ── Branch state ── */
  const [branches, setBranches] = useState<BranchItem[]>([]);
  const [editBranchDialog, setEditBranchDialog] = useState<BranchItem | null>(null);
  const [addBranchDialog, setAddBranchDialog] = useState(false);
  const [branchDraft, setBranchDraft] = useState<Partial<BranchItem>>({ name: "", region: "", address: "", status: "Active" });

  /* ── Billing ── */
  const [showPlanConfirm, setShowPlanConfirm] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  /* ── Reset Demo Data (beta) ── */
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const handleResetDemoData = () => {
    try {
      // Clear any persisted overrides — sample data re-seeds from sampleDataRegistry on next mount.
      const keysToClear = Object.keys(localStorage).filter(
        (k) => k.startsWith("koredor.") || k.startsWith("nav.") || k.startsWith("maverick.") || k.startsWith("cinematic.")
      );
      keysToClear.forEach((k) => localStorage.removeItem(k));
    } catch {
      // localStorage unavailable — proceed with reload anyway
    }
    toast.success("Demo data reset — reloading…");
    setResetConfirmOpen(false);
    setTimeout(() => window.location.reload(), 600);
  };

  /* ── Preferences state ── */
  const [prefs, setPrefs] = useState({
    emailNewJobs: true,
    issueEscalation: true,
    dailyDigest: false,
    autoAdvance: false,
    requireQA: true,
    subBidNotify: true,
    showFinancials: false,
    defaultTimezone: "America/New_York (EST)",
    defaultDateFormat: "MM/DD/YYYY",
    defaultBranch: "Tampa HQ",
    businessHours: "7:00 AM – 6:00 PM",
  });

  /* ── Branding actions ── */
  const handleSave = async () => {
    setSavingBranding(true);
    setBranding(draft);
    try {
      await SettingsService.upsert("branding", {
        companyName: draft.companyName,
        platformName: draft.platformName,
        tagline: draft.tagline,
        logoUrl: draft.logoUrl,
        primaryHue: draft.primaryHue,
        primarySaturation: draft.primarySaturation,
        primaryLightness: draft.primaryLightness,
        accentHue: draft.accentHue,
        accentSaturation: draft.accentSaturation,
        accentLightness: draft.accentLightness,
        darkMode: draft.darkMode,
      });
      toast.success("Branding saved");
    } catch (err) {
      console.warn("Failed to save branding to database:", err);
      toast.error("Failed to save branding");
    } finally {
      setSavingBranding(false);
    }
  };
  const handleReset = () => { setDraft(CCC_BLUE_DEFAULT); setBranding(CCC_BLUE_DEFAULT); toast.info("Branding reset"); };

  const previewStyle = (hue: number, sat: number, light: number) =>
    `hsl(${hue}, ${sat}%, ${light}%)`;

  const filteredUsers = users.filter((u) => {
    if (userRoleFilter !== "all" && u.role !== userRoleFilter) return false;
    if (userSearch && !u.name.toLowerCase().includes(userSearch.toLowerCase()) && !u.email.toLowerCase().includes(userSearch.toLowerCase())) return false;
    return true;
  });

  const roleLabelMap = Object.fromEntries(ROLES.map((r) => [r.key, r.label]));

  /* ── User actions ── */
  const handleEditUser = (user: UserItem) => {
    setEditUserDraft({ ...user });
    setEditUserDialog(user);
  };
  const handleSaveUser = async () => {
    if (!editUserDialog) return;
    try {
      // Note: full_name is a generated column, only update first_name/last_name
      const nameParts = editUserDraft.name?.trim().split(" ") || ["", ""];
      const updates: Partial<ProfileRow> = {
        first_name: nameParts[0] || "",
        last_name: nameParts.slice(1).join(" ") || "",
        role: editUserDraft.role,
        branch_name: editUserDraft.branch,
      };
      console.log("Updating profile with:", updates);
      await SettingsService.updateProfile(editUserDialog.id, updates);
      setUsers(prev => prev.map(u => u.id === editUserDialog.id ? { ...u, ...editUserDraft } as UserItem : u));
      setEditUserDialog(null);
      toast.success("User profile updated");
    } catch (err) {
      console.error("Failed to update user:", err);
      toast.error("Failed to update user profile");
    }
  };
  const handleChangeRole = (user: UserItem) => {
    setEditUserDraft({ ...user });
    setEditUserDialog(user);
  };
  const handleToggleUserStatus = async (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const newStatus = user.status === "Active" ? "Deactivated" : "Active";
    try {
      await SettingsService.updateProfileStatus(userId, newStatus);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
      toast.success(`User ${newStatus === "Active" ? "activated" : "deactivated"}`);
    } catch (err) {
      console.error("Failed to update user status:", err);
      toast.error("Failed to update user status");
    }
  };
  const handleResetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getPasswordResetRedirectUrl(),
      });
      if (error) {
        console.error("Password reset error:", error);
        toast.error("Failed to send password reset email");
      } else {
        toast.success("Password reset link sent to user's email");
      }
    } catch (err) {
      console.error("Password reset error:", err);
      toast.error("Failed to send password reset email");
    }
  };

  /* ── Branch actions ── */
  const handleSaveBranch = () => {
    if (editBranchDialog) {
      setBranches(prev => prev.map(b => b.id === editBranchDialog.id ? { ...editBranchDialog, ...branchDraft } as BranchItem : b));
      setEditBranchDialog(null);
      toast.success("Branch updated");
    }
  };
  const handleAddBranch = () => {
    if (!branchDraft.name?.trim()) return;
    const newBranch: BranchItem = {
      id: `b-${Date.now()}`,
      name: branchDraft.name || "",
      region: branchDraft.region || "",
      address: branchDraft.address || "",
      status: "Active",
    };
    setBranches(prev => [...prev, newBranch]);
    setBranchDraft({ name: "", region: "", address: "", status: "Active" });
    setAddBranchDialog(false);
    toast.success("Branch added");
  };
  const handleToggleBranch = (id: string) => {
    setBranches(prev => prev.map(b => b.id === id ? { ...b, status: b.status === "Active" ? "Inactive" : "Active" } : b));
    toast.success("Branch status updated");
  };

  /* ── Permission toggle ── */
  const handleTogglePerm = (roleKey: string, perm: string) => {
    if (roleKey === "super_admin" || roleKey === "owner") return; // protected
    setRolePerms(prev => {
      const current = prev[roleKey] || [];
      const has = current.includes(perm);
      return { ...prev, [roleKey]: has ? current.filter(p => p !== perm) : [...current, perm] };
    });
  };

  const handleSaveRolePerms = async () => {
    setSavingPerms(true);
    try {
      await SettingsService.saveRolePermissions(rolePerms);
      toast.success("Permissions saved");
    } catch (err) {
      console.error("[Settings] saveRolePermissions failed:", err);
      toast.error("Failed to save permissions");
    } finally {
      setSavingPerms(false);
    }
  };

  const handleResetRolePerms = async () => {
    setRolePerms(DEFAULT_ROLE_PERMISSIONS);
    try {
      await SettingsService.saveRolePermissions(DEFAULT_ROLE_PERMISSIONS);
      toast.info("Permissions reset to defaults");
    } catch {
      toast.error("Failed to reset permissions");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-bold tracking-tight">Settings — {TAB_TITLES[activeTab] || "Settings"}</h1>
        <p className="text-xs text-muted-foreground">Platform configuration, users, branding, and integrations</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-muted/60 flex-wrap h-auto gap-0.5 p-1">
          <TabsTrigger value="users" className="text-xs gap-1.5"><Users className="h-3.5 w-3.5" /> Users</TabsTrigger>
          {canManageRoles && <TabsTrigger value="roles" className="text-xs gap-1.5"><Shield className="h-3.5 w-3.5" /> Roles</TabsTrigger>}
          <TabsTrigger value="company" className="text-xs gap-1.5"><Building2 className="h-3.5 w-3.5" /> Company</TabsTrigger>
          <TabsTrigger value="branding" className="text-xs gap-1.5"><Palette className="h-3.5 w-3.5" /> Branding</TabsTrigger>
          <TabsTrigger value="preferences" className="text-xs gap-1.5"><SettingsIcon className="h-3.5 w-3.5" /> Preferences</TabsTrigger>
          <TabsTrigger value="billing" className="text-xs gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Billing</TabsTrigger>
          <TabsTrigger value="integrations" className="text-xs gap-1.5"><Plug className="h-3.5 w-3.5" /> Integrations</TabsTrigger>
          {canManageProducts && <TabsTrigger value="pricing" className="text-xs gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Pricing</TabsTrigger>}
          {canManageProducts && <TabsTrigger value="sku-mapping" className="text-xs gap-1.5"><Package className="h-3.5 w-3.5" /> SKU Mapping</TabsTrigger>}
          <TabsTrigger value="contracts" className="text-xs gap-1.5"><FileText className="h-3.5 w-3.5" /> Documents</TabsTrigger>
          <TabsTrigger value="audit" className="text-xs gap-1.5"><History className="h-3.5 w-3.5" /> Audit Log</TabsTrigger>
           {canManageProducts && <TabsTrigger value="safety" className="text-xs gap-1.5"><ShieldOff className="h-3.5 w-3.5" /> Validation & Alerts</TabsTrigger>}
           {canManageProducts && <TabsTrigger value="local-presence" className="text-xs gap-1.5"><PhoneOutgoing className="h-3.5 w-3.5" /> Local Presence</TabsTrigger>}
           {canManageProducts && <TabsTrigger value="financial-visibility" className="text-xs gap-1.5"><Eye className="h-3.5 w-3.5" /> Financial Visibility</TabsTrigger>}
           {canManageProducts && <TabsTrigger value="messaging-automation" className="text-xs gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Communication</TabsTrigger>}
           {canManageProducts && <TabsTrigger value="automation" className="text-xs gap-1.5"><Zap className="h-3.5 w-3.5" /> Automation</TabsTrigger>}
           {canManageProducts && <TabsTrigger value="routing" className="text-xs gap-1.5"><MapPin className="h-3.5 w-3.5" /> Routing & Dispatch</TabsTrigger>}
        </TabsList>

        {/* ═══ USERS TAB ═══ */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> User Management</CardTitle>
                  <CardDescription className="text-xs mt-1">{users.filter((u) => u.status === "Active").length} active users · {users.filter((u) => u.status === "Invited").length} pending invitations</CardDescription>
                </div>
                <PermissionGate permission="settings_edit">
                  <Button size="sm" className="text-xs h-8 gap-1.5" onClick={() => setInviteUserDialog(true)}><Plus className="h-3.5 w-3.5" /> Invite User</Button>
                </PermissionGate>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input placeholder="Search users..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="pl-8 text-xs h-8" />
                </div>
                <select value={userRoleFilter} onChange={(e) => setUserRoleFilter(e.target.value)} className="h-8 rounded-md border bg-background px-2 text-xs">
                  <option value="all">All Roles</option>
                  {ROLES.map((r) => (<option key={r.key} value={r.key}>{r.label}</option>))}
                </select>
              </div>

              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Name</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground hidden sm:table-cell">Email</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground">Role</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground hidden md:table-cell">Branch</th>
                      <th className="text-center py-2.5 px-4 font-medium text-muted-foreground">Status</th>
                      <th className="text-left py-2.5 px-4 font-medium text-muted-foreground hidden lg:table-cell">Last Login</th>
                      <th className="text-center py-2.5 px-3 font-medium text-muted-foreground w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-3 px-4"><div className="h-4 w-24 bg-muted animate-pulse rounded" /></td>
                          <td className="py-3 px-4 hidden sm:table-cell"><div className="h-4 w-32 bg-muted animate-pulse rounded" /></td>
                          <td className="py-3 px-4"><div className="h-5 w-16 bg-muted animate-pulse rounded" /></td>
                          <td className="py-3 px-4 hidden md:table-cell"><div className="h-4 w-20 bg-muted animate-pulse rounded" /></td>
                          <td className="py-3 px-4 text-center"><div className="h-5 w-16 bg-muted animate-pulse rounded mx-auto" /></td>
                          <td className="py-3 px-4 hidden lg:table-cell"><div className="h-4 w-20 bg-muted animate-pulse rounded" /></td>
                          <td className="py-3 px-3 text-center"><div className="h-6 w-6 bg-muted animate-pulse rounded" /></td>
                        </tr>
                      ))
                    ) : filteredUsers.length > 0 ? (
                      filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 px-4">
                          <p className="font-semibold">{u.name}</p>
                          <p className="text-[10px] text-muted-foreground sm:hidden">{u.email}</p>
                        </td>
                        <td className="py-2 px-4 text-muted-foreground hidden sm:table-cell">{u.email}</td>
                        <td className="py-2 px-4"><Badge variant="secondary" className="text-[10px]">{roleLabelMap[u.role] || u.role}</Badge></td>
                        <td className="py-2 px-4 text-muted-foreground hidden md:table-cell">{u.branch}</td>
                        <td className="py-2 px-4 text-center">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium border ${STATUS_STYLES[u.status]}`}>{u.status}</span>
                        </td>
                        <td className="py-2 px-4 text-muted-foreground hidden lg:table-cell tabular-nums">{u.lastLogin}</td>
                        <td className="py-2 px-3 text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onClick={() => handleEditUser(u)} className="text-xs gap-2">
                                <Pencil className="h-3 w-3" /> Edit Profile
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleChangeRole(u)} className="text-xs gap-2">
                                <Shield className="h-3 w-3" /> Change Role
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleToggleUserStatus(u.id)} className="text-xs gap-2">
                                {u.status === "Active" ? <><UserX className="h-3 w-3" /> Deactivate</> : <><UserCheck className="h-3 w-3" /> Activate</>}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleResetPassword(u.email)} className="text-xs gap-2">
                                <Lock className="h-3 w-3" /> Reset Password
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))
                    ) : (
                      <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No users found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ ROLES & PERMISSIONS TAB ═══ */}
        <TabsContent value="roles" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-sm flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> Roles & Permissions</CardTitle>
                  <CardDescription className="text-xs">Toggle permissions per role. Owner and Super Admin are protected.</CardDescription>
                </div>
                <PermissionGate permission="settings_edit">
                  <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={handleResetRolePerms} disabled={permsLoading || savingPerms}>
                    Reset to Defaults
                  </Button>
                </PermissionGate>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-x-auto">
                <table className="w-full text-[10px]">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground sticky left-0 bg-muted/40 min-w-[120px]">Permission</th>
                      {ROLES.map((r) => (
                        <th key={r.key} className="text-center py-2 px-2 font-medium text-muted-foreground min-w-[70px]">
                          <span className="block">{r.label}</span>
                          {(r.key === "super_admin" || r.key === "owner") && <Lock className="h-2.5 w-2.5 mx-auto mt-0.5 text-muted-foreground/50" />}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {permsLoading ? (
                      <tr><td colSpan={ROLES.length + 1} className="py-6 text-center text-xs text-muted-foreground">Loading permissions…</td></tr>
                    ) : PERMISSION_GROUPS.map((group) => (
                      <>
                        <tr key={group.section} className="border-b bg-muted/60">
                          <td colSpan={ROLES.length + 1} className="py-1.5 px-3 font-semibold text-[10px] uppercase tracking-wider text-muted-foreground sticky left-0">
                            {group.section}
                          </td>
                        </tr>
                        {group.perms.map((perm) => (
                          <tr key={perm} className="border-b last:border-0 hover:bg-muted/20">
                            <td className="py-1.5 px-3 pl-5 font-medium sticky left-0 bg-background">{perm}</td>
                            {ROLES.map((r) => {
                              const has = (rolePerms[r.key] || []).includes(perm);
                              const isProtected = r.key === "super_admin" || r.key === "owner";
                              return (
                                <td key={r.key} className="py-1.5 px-2 text-center">
                                  <button
                                    onClick={() => handleTogglePerm(r.key, perm)}
                                    disabled={isProtected}
                                    className={`transition-colors ${isProtected ? "cursor-not-allowed" : "cursor-pointer hover:opacity-70"}`}
                                  >
                                    {has ? (
                                      <CheckCircle className="h-3.5 w-3.5 text-success mx-auto" />
                                    ) : (
                                      <span className="text-muted-foreground/30">—</span>
                                    )}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <PermissionGate permission="settings_edit">
                  <Button size="sm" className="text-xs h-7" onClick={handleSaveRolePerms} disabled={permsLoading || savingPerms}>
                    {savingPerms ? "Saving…" : "Save Permissions"}
                  </Button>
                </PermissionGate>
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Super Admin and Owner roles cannot be modified
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ COMPANY TAB ═══ */}
        <TabsContent value="company" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><Building2 className="h-4 w-4 text-primary" /> Company Information</CardTitle>
              <CardDescription className="text-xs">Legal and operational details for your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-xs">Legal Company Name</Label><Input value={companyInfo.legalName} onChange={(e) => setCompanyInfo(c => ({ ...c, legalName: e.target.value }))} className="text-sm" /></div>
                <div className="space-y-2"><Label className="text-xs">DBA / Trade Name</Label><Input value={companyInfo.dbaName} onChange={(e) => setCompanyInfo(c => ({ ...c, dbaName: e.target.value }))} className="text-sm" /></div>
                <div className="space-y-2"><Label className="text-xs">EIN / Tax ID</Label><Input value={companyInfo.ein} onChange={(e) => setCompanyInfo(c => ({ ...c, ein: e.target.value }))} className="text-sm" /></div>
                <div className="space-y-2"><Label className="text-xs">License Number</Label><Input value={companyInfo.licenseNumber} onChange={(e) => setCompanyInfo(c => ({ ...c, licenseNumber: e.target.value }))} className="text-sm" /></div>
              </div>

              <div className="border-t pt-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-3">Primary Contact</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</Label><Input value={companyInfo.phone} onChange={(e) => setCompanyInfo(c => ({ ...c, phone: e.target.value }))} className="text-sm" /></div>
                  <div className="space-y-2"><Label className="text-xs flex items-center gap-1"><Mail className="h-3 w-3" /> Email</Label><Input value={companyInfo.email} onChange={(e) => setCompanyInfo(c => ({ ...c, email: e.target.value }))} className="text-sm" /></div>
                  <div className="space-y-2"><Label className="text-xs flex items-center gap-1"><Globe className="h-3 w-3" /> Website</Label><Input value={companyInfo.website} onChange={(e) => setCompanyInfo(c => ({ ...c, website: e.target.value }))} className="text-sm" /></div>
                  <div className="space-y-2"><Label className="text-xs flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</Label><Input value={companyInfo.address} onChange={(e) => setCompanyInfo(c => ({ ...c, address: e.target.value }))} className="text-sm" /></div>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">Branches & Regions</p>
                  <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => { setBranchDraft({ name: "", region: "", address: "", status: "Active" }); setAddBranchDialog(true); }}>
                    <Plus className="h-3 w-3" /> Add Branch
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {branchesLoading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="rounded-lg border p-3">
                        <div className="flex items-start justify-between mb-1">
                          <div>
                            <div className="h-4 w-24 bg-muted animate-pulse rounded mb-1" />
                            <div className="h-3 w-16 bg-muted animate-pulse rounded" />
                          </div>
                        </div>
                        <div className="h-3 w-32 bg-muted animate-pulse rounded mb-2" />
                        <div className="h-5 w-14 bg-muted animate-pulse rounded" />
                      </div>
                    ))
                  ) : branches.length > 0 ? (
                    branches.map((b) => (
                    <div key={b.id} className="rounded-lg border p-3">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <p className="text-xs font-semibold">{b.name}</p>
                          <p className="text-[10px] text-muted-foreground">{b.region}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0"><MoreHorizontal className="h-3 w-3" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-36">
                            <DropdownMenuItem className="text-xs gap-2" onClick={() => { setBranchDraft({ ...b }); setEditBranchDialog(b); }}>
                              <Pencil className="h-3 w-3" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-xs gap-2" onClick={() => handleToggleBranch(b.id)}>
                              {b.status === "Active" ? <><ToggleLeft className="h-3 w-3" /> Deactivate</> : <><ToggleRight className="h-3 w-3" /> Activate</>}
                            </DropdownMenuItem>
                            {branches.length > 1 && (
                              <DropdownMenuItem className="text-xs gap-2 text-destructive" onClick={() => { setBranches(prev => prev.filter(x => x.id !== b.id)); toast.success("Branch removed"); }}>
                                <Trash2 className="h-3 w-3" /> Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <p className="text-[10px] text-muted-foreground">{b.address}</p>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-medium mt-1.5 border ${b.status === "Active" ? "bg-success/10 text-success border-success/20" : "bg-muted text-muted-foreground border-border"}`}>
                        {b.status}
                      </span>
                    </div>
                  ))
                  ) : (
                    <p className="col-span-full py-6 text-center text-muted-foreground text-sm">No branches found</p>
                  )}
                </div>
              </div>

              <PermissionGate permission="settings_edit">
              <Button size="sm" disabled={savingCompany} onClick={async () => {
                setSavingCompany(true);
                try {
                  await SettingsService.upsert("company", {
                    ...companyInfo,
                    companyName: companyInfo.legalName || companyInfo.dbaName,
                    branches: [],
                    users: [],
                  });
                  toast.success("Company settings saved");
                } catch {
                  toast.error("Failed to save company settings");
                } finally {
                  setSavingCompany(false);
                }
              }}>{savingCompany ? "Saving…" : "Save Company Settings"}</Button>
              </PermissionGate>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ BRANDING TAB ═══ */}
        <TabsContent value="branding" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><Palette className="h-4 w-4 text-primary" /> Brand Identity</CardTitle>
              <CardDescription className="text-xs">Customize colors, logo, and platform name. Changes apply across the entire app.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Tagline first */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs">Company Name</Label>
                  <Input value={draft.companyName} onChange={(e) => setDraft({ ...draft, companyName: e.target.value })} className="text-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Platform Name</Label>
                  <Input value={draft.platformName} onChange={(e) => setDraft({ ...draft, platformName: e.target.value })} className="text-sm" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-xs">Tagline</Label>
                  <Input value={draft.tagline} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} className="text-sm" />
                </div>
              </div>

              {/* Logo */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Logo URL</Label>
                <Input placeholder="https://example.com/logo.png" value={draft.logoUrl || ""} onChange={(e) => setDraft({ ...draft, logoUrl: e.target.value || null })} className="text-sm" />
                {draft.logoUrl ? (
                  <div className="flex items-center gap-3 rounded-lg border p-3 bg-muted/20">
                    <img src={draft.logoUrl} alt="Logo preview" className="h-10 w-10 rounded-lg object-contain border" />
                    <div>
                      <p className="text-[10px] text-muted-foreground">Logo preview — displayed in sidebar header</p>
                      <button onClick={() => setDraft({ ...draft, logoUrl: null })} className="text-[10px] text-destructive hover:underline mt-0.5">Remove logo</button>
                    </div>
                  </div>
                ) : (
                  <div className="border-2 border-dashed rounded-lg p-4 text-center bg-muted/30">
                    <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1.5" />
                    <p className="text-[10px] text-muted-foreground">Enter a URL above or drag & drop (coming soon)</p>
                  </div>
                )}
              </div>

              {/* Colors + Live Preview side by side */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Primary Color */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-md border" style={{ background: previewStyle(draft.primaryHue, draft.primarySaturation, draft.primaryLightness) }} />
                    <Label className="text-xs font-semibold">Primary Color</Label>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between"><span className="text-[10px] text-muted-foreground">Hue</span><span className="text-[10px] font-mono">{draft.primaryHue}°</span></div>
                    <Slider value={[draft.primaryHue]} onValueChange={([v]) => setDraft({ ...draft, primaryHue: v })} min={0} max={360} step={1} />
                    <div className="flex items-center justify-between"><span className="text-[10px] text-muted-foreground">Saturation</span><span className="text-[10px] font-mono">{draft.primarySaturation}%</span></div>
                    <Slider value={[draft.primarySaturation]} onValueChange={([v]) => setDraft({ ...draft, primarySaturation: v })} min={0} max={100} step={1} />
                    <div className="flex items-center justify-between"><span className="text-[10px] text-muted-foreground">Lightness</span><span className="text-[10px] font-mono">{draft.primaryLightness}%</span></div>
                    <Slider value={[draft.primaryLightness]} onValueChange={([v]) => setDraft({ ...draft, primaryLightness: v })} min={10} max={60} step={1} />
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="h-5 w-5 rounded-md border" style={{ background: previewStyle(draft.accentHue, draft.accentSaturation, draft.accentLightness) }} />
                    <Label className="text-xs font-semibold">Accent Color</Label>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between"><span className="text-[10px] text-muted-foreground">Hue</span><span className="text-[10px] font-mono">{draft.accentHue}°</span></div>
                    <Slider value={[draft.accentHue]} onValueChange={([v]) => setDraft({ ...draft, accentHue: v })} min={0} max={360} step={1} />
                    <div className="flex items-center justify-between"><span className="text-[10px] text-muted-foreground">Saturation</span><span className="text-[10px] font-mono">{draft.accentSaturation}%</span></div>
                    <Slider value={[draft.accentSaturation]} onValueChange={([v]) => setDraft({ ...draft, accentSaturation: v })} min={0} max={100} step={1} />
                    <div className="flex items-center justify-between"><span className="text-[10px] text-muted-foreground">Lightness</span><span className="text-[10px] font-mono">{draft.accentLightness}%</span></div>
                    <Slider value={[draft.accentLightness]} onValueChange={([v]) => setDraft({ ...draft, accentLightness: v })} min={10} max={60} step={1} />
                  </div>
                </div>

                {/* Live Preview */}
                <div className="space-y-3">
                  <Label className="text-xs font-semibold">Live Preview</Label>
                  <div className="rounded-lg border p-4 bg-muted/20 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" style={{ backgroundColor: previewStyle(draft.primaryHue, draft.primarySaturation, draft.primaryLightness), color: "white" }}>Primary</Button>
                      <Button size="sm" variant="outline">Outline</Button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge style={{ backgroundColor: previewStyle(draft.primaryHue, draft.primarySaturation, draft.primaryLightness), color: "white" }}>Status</Badge>
                      <Badge variant="secondary">Secondary</Badge>
                      <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: previewStyle(draft.accentHue, draft.accentSaturation, draft.accentLightness) }}>
                        <SettingsIcon className="h-4 w-4 text-white" />
                      </div>
                    </div>
                    <div className="text-xs space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: previewStyle(draft.primaryHue, draft.primarySaturation, draft.primaryLightness) }} />
                        <span className="text-muted-foreground">Primary</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full" style={{ backgroundColor: previewStyle(draft.accentHue, draft.accentSaturation, draft.accentLightness) }} />
                        <span className="text-muted-foreground">Accent</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dark Mode */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="text-xs font-semibold">Dark Mode</p>
                  <p className="text-[10px] text-muted-foreground">Switch between light and dark interface</p>
                </div>
                <Switch checked={draft.darkMode} onCheckedChange={(v) => { setDraft({ ...draft, darkMode: v }); }} />
              </div>

              <div className="flex items-center gap-2">
                <PermissionGate permission="settings_edit">
                  <Button onClick={handleSave} disabled={savingBranding}>{savingBranding ? "Saving…" : "Save Branding"}</Button>
                </PermissionGate>
                <PermissionGate permission="settings_edit">
                  <Button variant="outline" onClick={handleReset}>Reset to Default</Button>
                </PermissionGate>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ PREFERENCES TAB ═══ */}
        <TabsContent value="preferences" className="space-y-4">
          {/* Reset Demo Data — beta safety net */}
          <Card className="border-warning/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Rocket className="h-4 w-4 text-warning" />
                Reset Demo Data
                <Badge variant="outline" className="text-[9px] px-1.5 py-0 border-warning/40 text-warning">Beta</Badge>
              </CardTitle>
              <CardDescription className="text-xs">
                Restore the original sample dataset. Use this if you've experimented and want a clean slate. Your real account data is unaffected.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                size="sm"
                variant="outline"
                className="border-warning/40 text-warning hover:bg-warning/10"
                onClick={() => setResetConfirmOpen(true)}
              >
                <RotateCcwIcon className="h-3.5 w-3.5 mr-1.5" />
                Reset to seed data
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><SettingsIcon className="h-4 w-4 text-primary" /> System Preferences</CardTitle>
              <CardDescription className="text-xs">Default behaviors and platform-wide settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: "emailNewJobs", label: "Email notifications for new jobs", desc: "Send email when a new job enters the pipeline", icon: Mail },
                { key: "issueEscalation", label: "Issue escalation alerts", desc: "Notify ops managers when urgent issues are created", icon: AlertTriangle },
                { key: "dailyDigest", label: "Daily scheduling digest", desc: "Send a summary of tomorrow's scheduled jobs each evening", icon: Calendar },
                { key: "autoAdvance", label: "Auto-advance job stages", desc: "Automatically move jobs forward when conditions are met", icon: Clock },
                { key: "requireQA", label: "Require QA before closeout", desc: "Prevent jobs from being closed without a submitted QA form", icon: FileText },
                { key: "subBidNotify", label: "Subcontractor bid notifications", desc: "Notify admins when new bids are received", icon: Bell },
                { key: "showFinancials", label: "Show financial data to all roles", desc: "Allow non-owner roles to see contract values and margins", icon: Eye },
              ].map((pref) => (
                <div key={pref.key} className="flex items-center justify-between rounded-lg border p-3">
                  <div className="flex items-center gap-3">
                    <pref.icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">{pref.label}</p>
                      <p className="text-[10px] text-muted-foreground">{pref.desc}</p>
                    </div>
                  </div>
                  <Switch
                    checked={(prefs as any)[pref.key]}
                    onCheckedChange={(v) => setPrefs(p => ({ ...p, [pref.key]: v }))}
                  />
                </div>
              ))}

              <div className="border-t pt-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-3">Defaults</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">Default Time Zone</Label>
                    <Select value={prefs.defaultTimezone} onValueChange={(v) => setPrefs(p => ({ ...p, defaultTimezone: v }))}>
                      <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="America/New_York (EST)">America/New_York (EST)</SelectItem>
                        <SelectItem value="America/Chicago (CST)">America/Chicago (CST)</SelectItem>
                        <SelectItem value="America/Denver (MST)">America/Denver (MST)</SelectItem>
                        <SelectItem value="America/Los_Angeles (PST)">America/Los_Angeles (PST)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Default Date Format</Label>
                    <Select value={prefs.defaultDateFormat} onValueChange={(v) => setPrefs(p => ({ ...p, defaultDateFormat: v }))}>
                      <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                        <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                        <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Default Branch for New Jobs</Label>
                    <Select value={prefs.defaultBranch} onValueChange={(v) => setPrefs(p => ({ ...p, defaultBranch: v }))}>
                      <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {branches.filter(b => b.status === "Active").map(b => (
                          <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Business Hours</Label>
                    <Input value={prefs.businessHours} onChange={(e) => setPrefs(p => ({ ...p, businessHours: e.target.value }))} className="text-sm" />
                  </div>
                </div>
              </div>

              <PermissionGate permission="settings_edit">
                <Button size="sm" disabled={savingPrefs} onClick={async () => {
                  setSavingPrefs(true);
                  try {
                    await SettingsService.upsert("preferences", {
                      emailNotifications: prefs.emailNewJobs,
                      issueEscalation: prefs.issueEscalation,
                      dailyDigest: prefs.dailyDigest,
                      autoAdvance: prefs.autoAdvance,
                      qaRequirements: prefs.requireQA,
                      timezone: prefs.defaultTimezone,
                      dateFormat: prefs.defaultDateFormat,
                      defaultBranch: prefs.defaultBranch,
                    });
                    toast.success("Preferences saved");
                  } catch {
                    toast.error("Failed to save preferences");
                  } finally {
                    setSavingPrefs(false);
                  }
                }}>{savingPrefs ? "Saving…" : "Save Preferences"}</Button>
              </PermissionGate>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ BILLING TAB ═══ */}
        <TabsContent value="billing" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" /> Billing & Subscription</CardTitle>
              <CardDescription className="text-xs">Manage your subscription plan and billing details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current plan */}
              <div className="rounded-lg border p-4 bg-primary/5 border-primary/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-bold">Professional Plan</p>
                    <p className="text-xs text-muted-foreground">Multi-branch operations platform</p>
                  </div>
                  <Badge className="bg-primary text-primary-foreground">Active</Badge>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { label: "Next Invoice", value: "$299/mo", detail: "Due April 1, 2026" },
                  { label: "Users", value: `${users.filter((u) => u.status === "Active").length} / 25`, detail: `${25 - users.filter((u) => u.status === "Active").length} seats available` },
                  { label: "Branches", value: `${branches.filter(b => b.status === "Active").length} / 10`, detail: `${10 - branches.filter(b => b.status === "Active").length} available` },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border p-3">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{item.label}</p>
                    <p className="text-lg font-bold">{item.value}</p>
                    <p className="text-[10px] text-muted-foreground">{item.detail}</p>
                  </div>
                ))}
              </div>

              {/* Plan comparison */}
              <div className="border-t pt-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-3">Available Plans</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {PLANS.map((plan) => {
                    const PlanIcon = plan.icon;
                    return (
                      <div key={plan.id} className={`rounded-lg border p-4 ${plan.current ? "border-primary/40 bg-primary/5 ring-1 ring-primary/20" : ""}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <PlanIcon className={`h-4 w-4 ${plan.current ? "text-primary" : "text-muted-foreground"}`} />
                          <p className="text-sm font-bold">{plan.name}</p>
                          {plan.current && <Badge className="text-[9px] bg-primary text-primary-foreground">Current</Badge>}
                        </div>
                        <p className="text-2xl font-bold">${plan.price}<span className="text-xs font-normal text-muted-foreground">/mo</span></p>
                        <p className="text-[10px] text-muted-foreground mt-1">{plan.users} users · {plan.branches} branches</p>
                        <ul className="mt-3 space-y-1">
                          {plan.features.map(f => (
                            <li key={f} className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                              <Check className="h-3 w-3 text-success shrink-0" /> {f}
                            </li>
                          ))}
                        </ul>
                        {!plan.current && (
                          <Button size="sm" variant="outline" className="w-full mt-3 text-xs" onClick={() => setShowPlanConfirm(plan.id)}>
                            {plan.price > 299 ? "Upgrade" : "Downgrade"}
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="border-t pt-4 flex items-center justify-between">
                <p className="text-[10px] text-muted-foreground">Need to cancel? Changes take effect at the end of your billing cycle.</p>
                <Button size="sm" variant="ghost" className="text-xs text-destructive" onClick={() => setShowCancelConfirm(true)}>Cancel Subscription</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ INTEGRATIONS TAB ═══ */}
        <TabsContent value="integrations" className="space-y-4">
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Plug className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground">External Integrations</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Connect your CRM, inventory, calendar, and accounting systems. Data syncs are designed to flow bi-directionally where supported.
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-[10px]">
                    <span className="flex items-center gap-1 text-muted-foreground"><Clock className="h-3 w-3" /> 0 Connected</span>
                    <span className="flex items-center gap-1 text-info"><CheckCircle className="h-3 w-3" /> 4 Ready</span>
                    <span className="flex items-center gap-1 text-muted-foreground"><AlertTriangle className="h-3 w-3" /> 2 Planned</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Integration sections */}
          {[
            {
              title: "CRM & Lead Management",
              icon: Globe,
              items: [
                { name: "GoHighLevel", desc: "All-in-one CRM — leads, pipelines, SMS, email, reputation", status: "available" as const, features: ["Lead sync", "Pipeline stages", "SMS/Email sync", "Review management"], dataFlow: "Bi-directional: Leads → Koredor · Job updates → GHL pipeline" },
                { name: "Salesforce", desc: "Enterprise CRM with custom objects and workflow automation", status: "planned" as const, features: ["Contact sync", "Opportunity tracking"], dataFlow: "Planned for enterprise tier" },
              ]
            },
            {
              title: "Inventory & Materials",
              icon: Package,
              items: [
                { name: "BoxHero", desc: "Cloud inventory management — SKU tracking, barcode scanning, stock alerts", status: "available" as const, features: ["SKU sync", "Stock levels", "Low stock alerts", "Barcode integration"], dataFlow: "Bi-directional: Stock levels → Warehouse Hub · Allocations → BoxHero" },
                { name: "QuickBooks Online", desc: "Accounting & inventory — invoices, payments, item tracking", status: "available" as const, features: ["Invoice sync", "Payment tracking", "Expense tracking", "Revenue reconciliation"], dataFlow: "Bi-directional: Job invoices → QBO · Payments → Job financials" },
              ]
            },
            {
              title: "Calendar & Scheduling",
              icon: Calendar,
              items: [
                { name: "Google Calendar", desc: "Sync install schedules, crew assignments, and appointments", status: "available" as const, features: ["Install date sync", "Crew calendar", "Reminders", "Notifications"], dataFlow: "Outbound: Job schedules → Calendar events" },
                { name: "Microsoft Outlook", desc: "Calendar and email integration for Microsoft 365", status: "planned" as const, features: ["Calendar sync", "Email integration"], dataFlow: "Planned for enterprise tier" },
              ]
            },
          ].map(section => (
            <Card key={section.title}>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2"><section.icon className="h-4 w-4 text-primary" /> {section.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {section.items.map(int => (
                  <div key={int.name} className="rounded-lg border overflow-hidden">
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center">
                          <section.icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-semibold">{int.name}</p>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${int.status === "available" ? "bg-info/10 text-info border border-info/20" : "bg-muted text-muted-foreground border border-border"}`}>
                              {int.status === "available" ? "Ready to Connect" : "Planned"}
                            </span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">{int.desc}</p>
                        </div>
                      </div>
                      {int.status === "available" ? (
                        <Button size="sm" variant="outline" className="text-[10px] h-7 gap-1" onClick={() => toast.info(`${int.name} configuration will be available with backend wiring`)}>
                          <Key className="h-3 w-3" /> Configure
                        </Button>
                      ) : (
                        <Badge variant="secondary" className="text-[10px]">Coming Soon</Badge>
                      )}
                    </div>
                    {int.status === "available" && (
                      <div className="px-3 pb-3 border-t bg-muted/10 pt-2.5">
                        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Sync Capabilities</p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {int.features.map(f => <span key={f} className="text-[9px] bg-muted rounded px-1.5 py-0.5 text-muted-foreground">{f}</span>)}
                        </div>
                        <p className="text-[10px] text-muted-foreground italic">{int.dataFlow}</p>
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}

          {/* API Access */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><Key className="h-4 w-4 text-primary" /> API Access</CardTitle>
              <CardDescription className="text-xs">Manage API keys for custom integrations and webhooks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/20">
                <div>
                  <p className="text-xs font-semibold">Production API Key</p>
                  <p className="text-[10px] text-muted-foreground font-mono">sk_live_••••••••••••••••</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-[10px] h-7"><Eye className="h-3 w-3" /></Button>
                  <Button size="sm" variant="outline" className="text-[10px] h-7" onClick={() => toast.info("API key regeneration will be available with backend")}>Regenerate</Button>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/20">
                <div>
                  <p className="text-xs font-semibold">Webhook Endpoint</p>
                  <p className="text-[10px] text-muted-foreground font-mono">https://api.koredor.com/webhooks/v1</p>
                </div>
                <Button size="sm" variant="outline" className="text-[10px] h-7 gap-1" onClick={() => toast.info("Webhook configuration will be available with backend")}>
                  <SettingsIcon className="h-3 w-3" /> Configure
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ PRICING TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="pricing" className="space-y-4">
            <PricingAdmin />
          </TabsContent>
        )}

        {/* ═══ SKU MAPPING TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="sku-mapping" className="space-y-4">
            <SkuMappingAdmin />
          </TabsContent>
        )}

        {/* ═══ DOCUMENTS & FORMS TAB ═══ */}
        <TabsContent value="contracts" className="space-y-4">
          <DocumentFormEngine />
        </TabsContent>

        {/* ═══ AUDIT LOG TAB ═══ */}
        <TabsContent value="audit" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Operations Audit Log</CardTitle>
              <CardDescription className="text-xs">All job-level changes recorded throughout Koredor operations</CardDescription>
            </CardHeader>
            <CardContent>
              {auditLoading ? (
                <p className="text-xs text-muted-foreground py-4 text-center">Loading audit log…</p>
              ) : liveAuditLog.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No audit entries found.</p>
              ) : (
                <div className="space-y-0 divide-y">
                  {liveAuditLog.map((entry) => {
                    const isCreate = entry.action === "create";
                    const AreaIcon = isCreate ? Plus : Pencil;
                    const iconColor = isCreate ? "text-success" : "text-primary";
                    const timestamp = new Date(entry.created_at).toLocaleString("en-US", {
                      month: "short", day: "numeric", year: "numeric",
                      hour: "numeric", minute: "2-digit",
                    });
                    return (
                      <div key={entry.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                        <div className="mt-0.5 rounded-md border p-1.5 bg-muted/30 shrink-0">
                          <AreaIcon className={`h-3.5 w-3.5 ${iconColor}`} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold">{entry.full_name ?? "System"}</span>
                            <Badge variant="outline" className="text-[9px] px-1.5 py-0 font-medium capitalize">{entry.action}</Badge>
                            <span className="text-[10px] text-muted-foreground font-mono">Job #{entry.job_id.slice(0, 8)}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{formatAuditSummary(entry)}</p>
                        </div>
                        <div className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {timestamp}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ═══ SAFETY & CONTROL TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="safety" className="space-y-4">
            <ValidationControlPanel />
          </TabsContent>
        )}

        {/* ═══ LOCAL PRESENCE TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="local-presence" className="space-y-4">
            <LocalPresenceAdmin />
          </TabsContent>
        )}

        {/* ═══ FINANCIAL VISIBILITY TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="financial-visibility" className="space-y-4">
            <FinancialVisibilitySettings />
          </TabsContent>
        )}

        {/* ═══ MESSAGING AUTOMATION TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="messaging-automation" className="space-y-4">
            <MessagingAutomationSettingsPanel />
          </TabsContent>
        )}

        {/* ═══ AUTOMATION TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="automation" className="space-y-4">
            <AutomationControlTab />
          </TabsContent>
        )}

        {/* ═══ ROUTING & DISPATCH TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="routing" className="space-y-4">
            <RoutingDispatchSettings />
          </TabsContent>
        )}

        {/* ═══ VENDOR LEAD TIMES TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="vendor-lead-times" className="space-y-4">
            <VendorLeadTimesAdmin />
          </TabsContent>
        )}

        {/* ═══ INSTALLER EXECUTION TAB ═══ */}
        {canManageProducts && (
          <TabsContent value="installer-execution" className="space-y-4">
            <InstallerExecutionSettings />
          </TabsContent>
        )}

      </Tabs>

      {/* ═══ Reset Demo Data Confirmation ═══ */}
      <Dialog open={resetConfirmOpen} onOpenChange={(o) => { setResetConfirmOpen(o); if (!o) setResetConfirmText(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <RotateCcwIcon className="h-4 w-4 text-warning" /> Reset demo data?
            </DialogTitle>
            <DialogDescription className="text-xs">
              This will clear local customizations (navigation overrides, mode settings) and reload the app with the original sample dataset. Your account, role, and tenant configuration are unaffected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-[11px]">Type <span className="font-mono font-bold">RESET</span> to confirm</Label>
            <Input
              value={resetConfirmText}
              onChange={(e) => setResetConfirmText(e.target.value)}
              placeholder="RESET"
              className="text-sm font-mono"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setResetConfirmOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={resetConfirmText !== "RESET"}
              onClick={handleResetDemoData}
            >
              Reset & Reload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ DIALOGS ═══ */}

      {/* Edit User Dialog */}
      <Dialog open={!!editUserDialog} onOpenChange={(open) => !open && setEditUserDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Edit User</DialogTitle>
            <DialogDescription className="text-xs">Update user profile, role, and branch assignment</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={editUserDraft.name || ""} onChange={(e) => setEditUserDraft(d => ({ ...d, name: e.target.value }))} className="text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input value={editUserDraft.email || ""} onChange={(e) => setEditUserDraft(d => ({ ...d, email: e.target.value }))} className="text-sm" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role</Label>
              <Select value={editUserDraft.role || ""} onValueChange={(v) => setEditUserDraft(d => ({ ...d, role: v as UserRole }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Branch</Label>
              <Select value={editUserDraft.branch || ""} onValueChange={(v) => setEditUserDraft(d => ({ ...d, branch: v }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {branches.filter(b => b.status === "Active").map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditUserDialog(null)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveUser}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteUserDialog} onOpenChange={(open) => { if (!open) { setInviteUserDialog(false); setInviteForm({ email: "", fullName: "", role: "viewer", branch: "" }); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Invite User</DialogTitle>
            <DialogDescription className="text-xs">Send an invitation to join your organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Email *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(d => ({ ...d, email: e.target.value }))}
                placeholder="colleague@company.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Full Name *</Label>
              <Input
                value={inviteForm.fullName}
                onChange={(e) => setInviteForm(d => ({ ...d, fullName: e.target.value }))}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Role *</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm(d => ({ ...d, role: v }))}>
                <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r.key} value={r.key} className="text-xs">{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Branch</Label>
              <Select value={inviteForm.branch} onValueChange={(v) => setInviteForm(d => ({ ...d, branch: v }))}>
                <SelectTrigger className="text-xs"><SelectValue placeholder="Select branch (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-xs">No branch</SelectItem>
                  {branches.filter(b => b.status === "Active").map(b => <SelectItem key={b.id} value={b.name} className="text-xs">{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setInviteUserDialog(false)} disabled={invitingUser}>Cancel</Button>
            <Button size="sm" onClick={async () => {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(inviteForm.email)) {
                toast.error("Please enter a valid email address");
                return;
              }
              if (!inviteForm.fullName.trim()) {
                toast.error("Please enter a full name");
                return;
              }
              const exists = users.find(u => u.email.toLowerCase() === inviteForm.email.toLowerCase());
              if (exists) {
                toast.error("A user with this email already exists");
                return;
              }
              setInvitingUser(true);
              try {
                let orgId = await getCurrentOrganizationId({ allowPendingSetup: true });
                
                // Generate new organization ID if still pending-setup (for first-time super_admin/owner)
                if (orgId === "pending-setup") {
                  orgId = crypto.randomUUID();
                  // Update current user's profile with the new organization
                  const { data: { user } } = await supabase.auth.getUser();
                  if (user) {
                    const { data: existingProfile } = await supabase
                      .from("profiles")
                      .select("*")
                      .eq("auth_user_id", user.id)
                      .maybeSingle();
                    
                    if (existingProfile) {
                      await supabase.from("profiles").update({
                        organization_id: orgId,
                      }).eq("auth_user_id", user.id);
                    } else {
                      // Get user email from auth metadata
                      const email = user.email || (user.user_metadata as any)?.email || "";
                      const fullName = (user.user_metadata as any)?.full_name || email.split("@")[0] || "User";
                      const nameParts = fullName.split(" ");
                      const firstName = nameParts[0] || "";
                      const lastName = nameParts.slice(1).join(" ") || "";
                      // Create a new profile for this user
                      await supabase.from("profiles").insert({
                        auth_user_id: user.id,
                        email: email,
                        first_name: firstName,
                        last_name: lastName,
                        status: "Active",
                        organization_id: orgId,
                      }).select().single().then(async ({ data, error }) => {
                        if (error) throw error;
                        if (data?.id) {
                          await SettingsService.syncProfileRole(data.id, "owner");
                        }
                      });
                    }
                  }
                }
                
                await SettingsService.inviteProfile(
                  inviteForm.email,
                  inviteForm.fullName,
                  inviteForm.role,
                  inviteForm.branch || undefined,
                  orgId
                );
                toast.success("Invitation sent successfully");
                setInviteUserDialog(false);
                setInviteForm({ email: "", fullName: "", role: "viewer", branch: "" });
                void refreshUsers();
              } catch (err: any) {
                console.error("Failed to invite user:", err);
                if (err?.message?.includes("organization_id")) {
                  toast.error("Your account is not linked to an organization. Please contact your administrator.");
                } else {
                  toast.error("Failed to send invitation");
                }
              } finally {
                setInvitingUser(false);
              }
            }} disabled={invitingUser}>
              {invitingUser ? "Inviting..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Branch Dialog */}
      <Dialog open={!!editBranchDialog} onOpenChange={(open) => !open && setEditBranchDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Edit Branch</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={branchDraft.name || ""} onChange={(e) => setBranchDraft(d => ({ ...d, name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Region</Label><Input value={branchDraft.region || ""} onChange={(e) => setBranchDraft(d => ({ ...d, region: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Address</Label><Input value={branchDraft.address || ""} onChange={(e) => setBranchDraft(d => ({ ...d, address: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditBranchDialog(null)}>Cancel</Button>
            <Button size="sm" onClick={handleSaveBranch}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Branch Dialog */}
      <Dialog open={addBranchDialog} onOpenChange={setAddBranchDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">Add Branch</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Name *</Label><Input value={branchDraft.name || ""} onChange={(e) => setBranchDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Naples" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Region</Label><Input value={branchDraft.region || ""} onChange={(e) => setBranchDraft(d => ({ ...d, region: e.target.value }))} placeholder="e.g. Southwest" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Address</Label><Input value={branchDraft.address || ""} onChange={(e) => setBranchDraft(d => ({ ...d, address: e.target.value }))} placeholder="Full address" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAddBranchDialog(false)}>Cancel</Button>
            <Button size="sm" onClick={handleAddBranch}>Add Branch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Change Confirmation */}
      <Dialog open={!!showPlanConfirm} onOpenChange={(open) => !open && setShowPlanConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Change Plan</DialogTitle>
            <DialogDescription className="text-xs">
              {showPlanConfirm && (() => {
                const plan = PLANS.find(p => p.id === showPlanConfirm);
                return plan ? `Switch to ${plan.name} at $${plan.price}/mo? Changes take effect at the start of your next billing cycle.` : "";
              })()}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowPlanConfirm(null)}>Cancel</Button>
            <Button size="sm" onClick={() => { setShowPlanConfirm(null); toast.success("Plan change request submitted. Changes take effect next billing cycle."); }}>Confirm Change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Confirmation */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm text-destructive">Cancel Subscription</DialogTitle>
            <DialogDescription className="text-xs">
              Are you sure you want to cancel? Your account will remain active until the end of your current billing cycle. All data will be retained for 90 days.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowCancelConfirm(false)}>Keep Subscription</Button>
            <Button size="sm" variant="destructive" onClick={() => { setShowCancelConfirm(false); toast.success("Cancellation request submitted. Access continues until end of billing cycle."); }}>Confirm Cancellation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
